import React, { ChangeEvent, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocale } from "lib/hooks/useLocale";
import {
  ArrowLeft,
  ArrowRight,
  CreditCard,
  Info,
  Storefront,
  Tag,
  Ticket,
  Truck,
  WarningCircle,
  Bank,
} from "@phosphor-icons/react";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents } from "lib/utils/constants";
import { AppliedCouponType } from "lib/contexts/CheckoutProvider";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import { isOnlyFreebieAutoApplied } from "lib/utils/checkout";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import CalloutCard from "components/coupons/cards/CalloutCard";
import CouponSummaryStrip from "components/common/CouponSummaryStrip.tsx";
import { classNames } from "lib/utils/helpers.ts";
import {
  categorizeCoupons,
  getAppliedCouponCategory,
  isValidPartnerOffer,
  mapRewardDescription,
  mapRewardHeader,
  mapRewardLogo,
  mapRewardTitle,
  type CouponDialogProps,
} from "lib/utils/coupons.ts";
import ProductSelectorCard from "../coupons/ProductSelectorCard.tsx";
import CouponCard from "../coupons/CouponCard.tsx";
import { Icon, PhosphorIcon } from "@shopflo/ui/icon";
import { Input } from "@shopflo/ui/input";
import {
  CouponPageConfigType,
  PartnerOfferType,
  PartnerOffersDisplay,
} from "lib/types/merchantConfigTypes.ts";
import { useMerchantContext } from "lib/contexts/MerchantProvider.tsx";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { getTextColorByBrightness, hexToRgba } from "@shopflo/ui/utils";
import { useSearchParams } from "react-router-dom";
import { currencyFormatter } from "lib/utils/formatters.ts";
import BankOffersCard from "components/coupons/cards/BankOffersCard.tsx";
import PartnerOffersCard from "components/coupons/cards/PartnerOffersCard.tsx";
import PartnerOffersStickyFooter from "components/coupons/PartnerOffersStickyFooter.tsx";
import BrandLogoImage from "components/common/loaders/BrandLogoImage";
import { DiscountAchieverProvider, useDiscountAchieverContext } from "lib/contexts/DiscountAchieverProvider";
import { ImpactedEntity } from "lib/types/discountAchiever";
import { computeBlockFulfillment } from "lib/utils/discountAchieverFulfillment";
import { useItemEdit } from "lib/hooks/useItemEdit";
import { useLoader } from "lib/contexts/LoaderContext";
import { errorToast } from "lib/utils/toasters";
import FamilyTray from "components/discount-achiever/FamilyTray.tsx";

const buildCouponFilterConfig = (
  partnerOffersLabel: string,
): Record<string, { label: string; iconComponent?: PhosphorIcon }> => ({
  ALL: { label: "All" },
  BRAND_OFFERS: { label: "Brand Offers", iconComponent: Storefront },
  PAYMENT_OFFERS: { label: "Payment Offers", iconComponent: CreditCard },
  SHIPPING_OFFERS: { label: "Shipping Offers", iconComponent: Truck },
  PG_OFFERS: { label: "PG Offers", iconComponent: Bank },
  PARTNER_OFFERS: { label: partnerOffersLabel, iconComponent: Ticket },
  UNAVAILABLE: { label: "Unavailable" },
});

function getCouponFilterIcon(
  category: string,
  primaryColor: string,
  filterConfig: Record<string, { label: string; iconComponent?: PhosphorIcon }>,
) {
  const config = filterConfig[category];
  if (!config?.iconComponent) return null;

  return (
    <Icon
      IconComponent={config.iconComponent}
      size="xs"
      iconProps={{
        color: primaryColor,
      }}
    />
  );
}

const ReplaceCouponDialog = React.lazy(() => import("../../checkout/dialogs/ReplaceCouponDialog.tsx"));

// Renders a single reward discount card
const RewardDiscountCard: React.FC<{
  discount: AppliedCouponType;
  onDelete: (code: string) => void;
  t: any;
}> = ({ discount, onDelete, t }) => {
  return (
    <div className="mx-3 mb-3 overflow-hidden rounded-2xl border border-gray-light">
      <div className="rounded-[14px] ">
        <div className="to-[#94949400)] bg-gradient-to-b from-[#9494941A] from-0% to-100%">
          <div className="flex items-center justify-between px-3 py-5 text-sm font-medium text-yay-dark">
            <div className="flex items-center space-x-2 text-coal-dark">
              <img
                src={mapRewardLogo(discount.couponRewardConfig?.walletType ?? "CRED")}
                alt={`${discount.couponRewardConfig?.walletType ?? "CRED"}-logo`}
                className={`rounded-[2px]`}
              />
              <span className="text-base font-semibold">
                {mapRewardTitle(discount.couponRewardConfig?.walletType ?? "CRED")}
              </span>
            </div>
            {discount.couponRewardConfig?.walletType === "TICKERTAPE" ? (
              <span
                className="text-xs text-coal-dark font-semibold underline cursor-pointer"
                onClick={() => onDelete(discount.code)}>
                {t("remove")}
              </span>
            ) : (
              <span className="text-xs font-semibold">{t("unlocked")}</span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 px-3 pb-2 text-sm font-medium text-coal-dark">
          <div className="text-base font-semibold text-yay-dark">
            {mapRewardHeader(
              discount.couponRewardConfig?.walletType ?? "CRED",
              discount.couponRewardConfig?.amount ?? 0,
            )}
          </div>
          <div className="text-xs font-medium text-coal-dark">
            {mapRewardDescription(discount.couponRewardConfig?.walletType ?? "CRED")}
          </div>
        </div>

        {Boolean(discount?.rewardData?.longDescription?.length) ? (
          <div className="px-3">
            <details className="group pb-3 text-[#4d4d4d]">
              <summary className="flex select-none list-none items-center text-xs font-semibold group-open:hidden">
                {t("more")}
              </summary>
              <ul className="flex w-full flex-col space-y-1 rounded-lg bg-gray-lighter px-2 py-3">
                {discount?.rewardData?.longDescription.map((item: string, index: number) => (
                  <li
                    className="ml-4 list-disc text-xs font-normal text-coal-light"
                    key={`${item}--${index}`}>
                    {item}
                  </li>
                ))}
              </ul>
            </details>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// Renders the coupon filter buttons
const CouponFilterButtons: React.FC<{
  groupedCoupons: Array<{ category: string; coupons: any[] }>;
  activeFilter: string;
  onFilterChange: (filter: string) => void;
  primaryColor: string;
  customisations?: CouponPageConfigType;
  filterConfig: Record<string, { label: string; iconComponent?: PhosphorIcon }>;
}> = ({ groupedCoupons, activeFilter, onFilterChange, primaryColor, customisations, filterConfig }) => {
  return (
    <div className="flex gap-1.5 overflow-x-auto scrollbar-hide mb-1">
      {groupedCoupons.map(({ category }) => {
        const config = filterConfig[category];
        return (
          <button
            key={category}
            onClick={() => onFilterChange(category)}
            className={classNames(
              "flex items-center gap-1.5 h-[25px] px-2 border border-zinc-950 border-opacity-12 text-text-xxs-medium whitespace-nowrap focus:outline-none",
              activeFilter === category
                ? "border-primary-dark bg-primary-lighter text-black"
                : "border-gray-light text-coal-light",
            )}
            style={{
              backgroundColor: activeFilter === category ? hexToRgba(primaryColor, 0.1) : "transparent",
              borderColor:
                activeFilter === category
                  ? hexToRgba(primaryColor, 0.4)
                  : getTextColorByBrightness({
                      bgHexColor: customisations?.backgroundColor ?? primaryColor,
                      lightOpacity: 24,
                    }),
              borderRadius: customisations?.filterRadius ?? 6,
            }}>
            {getCouponFilterIcon(category, primaryColor, filterConfig)}
            {config?.label}
          </button>
        );
      })}
    </div>
  );
};

// Renders the loading skeleton for coupons
const CouponLoadingState: React.FC = () => {
  return (
    <ul className="flex flex-col space-y-2">
      <li key="coupon-loader-1">
        <CouponCardSkeletonLoader />
      </li>
      <li key="coupon-loader-2">
        <CouponCardSkeletonLoader />
      </li>
    </ul>
  );
};

// Renders the product selector discounts section
const ProductSelectorSection: React.FC<{
  productSelectorDiscounts: AppliedCouponType[];
  t: any;
  customisations?: CouponPageConfigType;
}> = ({ productSelectorDiscounts, t, customisations }) => {
  if (!productSelectorDiscounts.length) return null;

  return (
    <div className="flex flex-col space-y-2">
      <ul className="flex flex-col space-y-2">
        {productSelectorDiscounts?.map((coupon: AppliedCouponType) => (
          <ProductSelectorCard coupon={coupon} key={coupon.code} customisations={customisations} />
        ))}
      </ul>
    </div>
  );
};

// Renders available coupon cards
const AvailableCouponsSection: React.FC<{
  filteredCoupons: any[];
  onApplyClick: (couponCode: string, isStackable: boolean, couponTitle: string) => Promise<void>;
  nonRewardAppliedCoupons: AppliedCouponType[];
  customisations?: CouponPageConfigType;
}> = ({ filteredCoupons, onApplyClick, nonRewardAppliedCoupons, customisations }) => {
  const hasAvailableCoupons = filteredCoupons?.some(
    (coupon: any) =>
      coupon?.coupon_details?.coupon_type !== "CALLOUT_CARD" &&
      coupon?.coupon_details?.coupon_type !== "PG_OFFERS" &&
      (coupon.applicable ||
        coupon?.un_applicability_reason?.reason === "UARC_002" ||
        coupon?.un_applicability_reason?.reason === "UARC_007"),
  );

  if (!hasAvailableCoupons) return null;

  return (
    <>
      <p className="pb-3 pt-5 text-text-xxs-semibold uppercase text-coal-light">Available coupons</p>
      <ul className="flex flex-col gap-3">
        {filteredCoupons?.map((coupon: any) => {
          // show only non callout coupons that are either applicable or combinable by reason UARC_002/007
          const show =
            coupon?.coupon_details?.coupon_type !== "CALLOUT_CARD" &&
            coupon?.coupon_details?.coupon_type !== "PG_OFFERS" &&
            (coupon.applicable ||
              coupon?.un_applicability_reason?.reason === "UARC_002" ||
              coupon?.un_applicability_reason?.reason === "UARC_007");
          if (!show) return null;

          return (
            <li key={coupon.coupon_details?.coupon_code}>
              <CouponCard
                isApplicable={
                  coupon.applicable ||
                  coupon?.un_applicability_reason?.reason === "UARC_007" ||
                  coupon?.un_applicability_reason?.reason === "UARC_002"
                }
                couponType={coupon.coupon_details?.coupon_type}
                couponCode={coupon.coupon_details?.coupon_code}
                couponHeader={coupon.coupon_details?.header}
                couponTitle={coupon.coupon_details?.title}
                couponDesc={coupon.coupon_details?.description}
                moreInfo={coupon.coupon_details?.long_description}
                unApplicabilityReason={coupon.un_applicability_reason?.description}
                isStackable={Boolean(coupon?.un_applicability_reason?.reason === "UARC_002")}
                totalDiscount={coupon?.discount_attribution?.total_discount}
                isAutoApplicable={coupon?.coupon_details?.auto_applicable}
                onApplyClick={onApplyClick}
                nonRewardAppliedCoupons={nonRewardAppliedCoupons}
                customisations={customisations}
              />
            </li>
          );
        })}
      </ul>
    </>
  );
};

// Renders Bank Offers section if enabled
const BankOffersSection: React.FC<{
  filteredCoupons: any[];
  shouldShow: boolean;
  customisations?: CouponPageConfigType;
}> = ({ filteredCoupons, shouldShow, customisations }) => {
  const { t } = useLocale();
  const hasBankOffers = filteredCoupons?.some(
    (coupon: any) => coupon?.coupon_details?.coupon_type === "PG_OFFERS",
  );

  if (!shouldShow || !hasBankOffers) return null;
  return (
    <>
      <p className="pb-3 pt-5 text-text-xxs-semibold uppercase text-coal-light">{t("bank_offers")}</p>

      <ul className="flex flex-col gap-3">
        {filteredCoupons?.map((coupon: any) => {
          if (coupon?.coupon_details?.coupon_type !== "PG_OFFERS") return null;

          return (
            <li key={coupon.coupon_details?.coupon_code}>
              <BankOffersCard
                couponTitle={coupon.coupon_details?.title}
                couponHeader={coupon.coupon_details?.header}
                couponDescription={coupon.coupon_details?.description}
                couponLongDescription={coupon.coupon_details?.long_description}
                bankKey={coupon.coupon_details?.bank_key}
                customisations={customisations}
              />
            </li>
          );
        })}
      </ul>
    </>
  );
};

// Renders Partner Offers section
const PartnerOffersSection: React.FC<{
  partnerOffers: PartnerOfferType[];
  partnerOffersDisplay?: PartnerOffersDisplay | null;
  activeFilter: string;
  onCollect: (offerId: string) => void;
  onRemove: (offerId: string) => void;
  customisations?: CouponPageConfigType;
  branding?: Record<string, { logo_url: string; display_name: string }>;
}> = ({
  partnerOffers,
  partnerOffersDisplay,
  activeFilter,
  onCollect,
  onRemove,
  customisations,
  branding,
}) => {
  const { t } = useLocale();
  const visible = activeFilter === "ALL" || activeFilter === "PARTNER_OFFERS";
  if (!visible || !partnerOffers?.length) return null;

  const isCollected = (state: string) => state === "COLLECTED" || state === "ALREADY_APPLIED";
  const allCollected = partnerOffers.every((o) => isCollected(o.offer_state));
  const noneCollected = partnerOffers.every((o) => !isCollected(o.offer_state));
  const displayState = allCollected
    ? partnerOffersDisplay?.all_collected
    : noneCollected
    ? partnerOffersDisplay?.none_collected
    : partnerOffersDisplay?.partial_collected;

  const sectionTitle =
    activeFilter === "ALL"
      ? t("partner_offers.section_title")
      : displayState?.title ?? t("partner_offers.section_title");
  const highlights = displayState?.highlights ?? [];
  // highlights with a logo are brand-specific and map to offers by index
  const brandHighlights = highlights.filter((h) => h.logo);

  return (
    <div className="antialiased">
      {activeFilter === "ALL" ? (
        <p className="pb-3 pt-5 text-text-xxs-semibold uppercase text-coal-light">{sectionTitle}</p>
      ) : (
        <div className="flex w-full flex-col items-start self-stretch px-2 pb-2">
          <p className="text-text-md-semibold text-black">{sectionTitle}</p>
        </div>
      )}
      {activeFilter !== "ALL" && (
        <div className="mb-3 flex items-center gap-1 self-stretch rounded-lg border border-[rgba(178,128,0,0.12)] bg-[rgba(178,128,0,0.08)] px-1.5 py-1">
          <Info className="h-3.5 w-3.5 shrink-0 text-amber-dark" />
          <p className="text-text-xs-medium text-amber-dark">{t("partner_offers.prepaid_only_notice")}</p>
        </div>
      )}
      <ul className="flex flex-col gap-3 px-px">
        {partnerOffers.map((offer, i) => {
          const highlight = brandHighlights[i];
          const brandLabel = offer.partner_name ?? highlight?.text?.split(" | ")[0];
          return (
            <li key={offer.offer_id}>
              <PartnerOffersCard
                offerId={offer.offer_id}
                title={offer.title}
                description={offer.description}
                logoUrl={offer.logo_url ?? highlight?.logo}
                tncs={offer.tncs ?? []}
                offerState={offer.offer_state}
                offerCode={offer.offer_code}
                minimumOrderValue={offer.minimum_order_value}
                brandLabel={brandLabel}
                onCollect={onCollect}
                onRemove={onRemove}
                customisations={customisations}
              />
            </li>
          );
        })}
      </ul>
      {activeFilter !== "ALL" && branding && Object.keys(branding).length > 0 && (
        <div className="flex items-center gap-1 self-stretch px-2 p-3">
          <p className="text-text-xxs-medium text-black/50">{t("partner_offers.powered_by")}</p>
          <div className="flex items-center gap-2">
            {Object.values(branding).map((brand) => (
              <BrandLogoImage
                key={brand.display_name}
                src={brand.logo_url}
                alt={brand.display_name}
                className="h-4 object-contain"
                skeletonClassName="h-4 w-14 rounded-sm"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Renders callout cards (applicable and not applicable)
const CalloutCardsSection: React.FC<{
  filteredCoupons: any[];
  t: any;
  customisations?: CouponPageConfigType;
}> = ({ filteredCoupons, t, customisations }) => {
  const hasCalloutCards = filteredCoupons?.some(
    (coupon: any) => coupon?.coupon_details?.coupon_type === "CALLOUT_CARD",
  );

  if (!hasCalloutCards) return null;

  const applicableCalloutCards = filteredCoupons.filter(
    (coupon: any) => coupon?.applicable && coupon?.coupon_details?.coupon_type === "CALLOUT_CARD",
  );
  const notApplicableCalloutCards = filteredCoupons.filter(
    (coupon: any) => !coupon?.applicable && coupon?.coupon_details?.coupon_type === "CALLOUT_CARD",
  );

  return (
    <>
      <p className="pb-3 pt-5 text-text-xxs-semibold uppercase text-coal-light">Additional offers</p>
      <div className="flex flex-col gap-3">
        {applicableCalloutCards.length > 0 && (
          <ul className="flex flex-col gap-3">
            {applicableCalloutCards.map((coupon: any) => {
              return (
                <li key={coupon.coupon_details?.coupon_code}>
                  <CalloutCard
                    isApplicable={coupon?.applicable}
                    couponTitle={coupon.coupon_details?.title}
                    couponHeader={coupon.coupon_details?.header}
                    couponDescription={coupon.coupon_details?.description}
                    couponLongDescription={coupon.coupon_details?.long_description}
                    unApplicabilityReason={coupon.un_applicability_reason?.description}
                    customisations={customisations}
                  />
                </li>
              );
            })}
          </ul>
        )}

        {/* Not applicable callout cards */}
        {notApplicableCalloutCards.length > 0 && (
          <ul className="flex flex-col gap-3">
            {notApplicableCalloutCards.map((coupon: any) => {
              return (
                <li key={coupon.coupon_details?.coupon_code}>
                  <CalloutCard
                    isApplicable={coupon?.applicable}
                    couponTitle={coupon.coupon_details?.title}
                    couponHeader={coupon.coupon_details?.header}
                    couponDescription={coupon.coupon_details?.description}
                    couponLongDescription={coupon.coupon_details?.long_description}
                    unApplicabilityReason={coupon.un_applicability_reason?.description}
                    customisations={customisations}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
};

// Renders unavailable coupons section
const UnavailableCouponsSection: React.FC<{
  filteredCoupons: any[];
  shouldShow: boolean;
  onApplyClick: (couponCode: string, isStackable: boolean, couponTitle: string) => Promise<void>;
  nonRewardAppliedCoupons: AppliedCouponType[];
  customisations?: CouponPageConfigType;
  t: any;
}> = ({ filteredCoupons, shouldShow, onApplyClick, nonRewardAppliedCoupons, t, customisations }) => {
  if (!shouldShow) return null;

  const hasUnavailableCoupons = filteredCoupons?.some(
    (coupon: any) =>
      coupon?.coupon_details?.coupon_type !== "CALLOUT_CARD" &&
      coupon?.coupon_details?.coupon_type !== "PG_OFFERS" &&
      !coupon?.applicable &&
      coupon?.un_applicability_reason?.reason !== "UARC_002" &&
      coupon?.un_applicability_reason?.reason !== "UARC_007",
  );

  if (!hasUnavailableCoupons) return null;

  return (
    <>
      <p className="pb-3 pt-5 text-text-xxs-semibold uppercase text-coal-light">Unavailable coupons</p>
      <ul className="flex flex-col gap-3 ">
        {filteredCoupons?.map((coupon: any) => {
          // unavailable logic: non-callout, not applicable, not UARC_002/007
          const isUnavailable =
            coupon?.coupon_details?.coupon_type !== "CALLOUT_CARD" &&
            coupon?.coupon_details?.coupon_type !== "PG_OFFERS" &&
            !coupon?.applicable &&
            coupon?.un_applicability_reason?.reason !== "UARC_002" &&
            coupon?.un_applicability_reason?.reason !== "UARC_007";

          if (!isUnavailable) return null;

          return (
            <li key={coupon.coupon_details?.coupon_code}>
              <CouponCard
                couponType={coupon.coupon_details?.coupon_type}
                isApplicable={coupon.applicable}
                couponCode={coupon.coupon_details?.coupon_code}
                couponHeader={coupon.coupon_details?.header}
                couponTitle={coupon.coupon_details?.title}
                couponDesc={coupon.coupon_details?.description}
                moreInfo={coupon.coupon_details?.long_description}
                unApplicabilityReason={coupon.un_applicability_reason?.description}
                isStackable={Boolean(coupon?.un_applicability_reason?.reason === "UARC_002")}
                totalDiscount={coupon?.discount_attribution?.total_discount}
                isAutoApplicable={coupon?.coupon_details?.auto_applicable}
                nonRewardAppliedCoupons={nonRewardAppliedCoupons}
                onApplyClick={onApplyClick}
                customisations={customisations}
                isAchievable={coupon.un_applicability_reason?.impacted_entities?.length > 0}
              />
            </li>
          );
        })}
      </ul>
    </>
  );
};

const CouponDialogContent: React.FC<CouponDialogProps> = ({
  setOpenDialog,
  handleDiscountApply,
  handleDeleteCoupon,
  savingItems,
  couponDisplayType,
  isCouponsLoading,
  couponApplyError,
  setCouponApplyError,
  initialFilter = "ALL",
  onCollectPartnerOffer,
  onRemovePartnerOffer,
}) => {
  const [searchParams] = useSearchParams();
  const { t } = useLocale();
  const {
    state: { coupons, bankOffers, partnerOffers, partnerOffersDisplay, appliedCoupons, billing },
  } = useCheckoutContext();
  const {
    state: { cartMetadata, checkoutMetadata, merchant },
  } = useMerchantContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const [activeFilter, setActiveFilter] = useState<string>(initialFilter);
  useEffect(() => {
    // INPUT + PARTNER_OFFERS is only opened from PartnerOffersStrip — lock to that tab
    // (and hide filter buttons). Other openers (e.g. ViewOtherCouponsTile with ALL)
    // must still reach the content they requested.
    if (couponDisplayType === "INPUT" && initialFilter === "PARTNER_OFFERS") {
      setActiveFilter(isAuthenticated ? "PARTNER_OFFERS" : "ALL");
      return;
    }
    const nextFilter = initialFilter ?? "ALL";
    setActiveFilter(nextFilter === "PARTNER_OFFERS" && !isAuthenticated ? "ALL" : nextFilter);
  }, [initialFilter, isAuthenticated, couponDisplayType]);
  const [manualCoupon, setManualCoupon] = useState<string>("");
  const [openReplaceCouponDialog, setOpenReplaceCouponDialog] = useState<boolean>(false);
  const [selectedCoupon, setSelectedCoupon] = useState<string>("");

  const isPartnerOffersOnlyMode = couponDisplayType === "INPUT" && initialFilter === "PARTNER_OFFERS";
  const [achiever, setAchiever] = useState<{
    open: boolean;
    couponName: string;
    couponCode: string;
    submitting: boolean;
  }>({ open: false, couponName: "", couponCode: "", submitting: false });
  const achieverClosedByCompletionRef = useRef(false);
  const {
    state: achieverState,
    actions: { fetchInitialBlocks, reset: resetAchiever },
  } = useDiscountAchieverContext();
  const { handleAddItemWithResult } = useItemEdit();
  const { setIsLoading: setOverlayLoading } = useLoader();
  const visiblePartnerOffers = isAuthenticated ? (partnerOffers ?? []).filter(isValidPartnerOffer) : [];

  const groupedCoupons = useMemo(
    () => categorizeCoupons(coupons || [], bankOffers || [], visiblePartnerOffers),
    [coupons, bankOffers, visiblePartnerOffers],
  );

  const filteredCoupons = useMemo(() => {
    if (activeFilter === "ALL") return [...(coupons ?? []), ...(bankOffers ?? [])];
    const group = groupedCoupons.find((groupedCoupon) => groupedCoupon.category === activeFilter);
    return group?.coupons || [];
  }, [activeFilter, groupedCoupons, coupons, bankOffers]);

  const nonRewardAppliedCoupons = useMemo(
    () => (appliedCoupons || []).filter((coupon) => !coupon.isReward),
    [appliedCoupons],
  );

  const rewardDiscounts = useMemo(
    () => (appliedCoupons || []).filter((coupon) => coupon.isReward),
    [appliedCoupons],
  );

  const productSelectorDiscounts = useMemo(
    () =>
      (appliedCoupons || []).filter(
        (coupon) =>
          coupon.isProductSelector &&
          (activeFilter === "ALL" || getAppliedCouponCategory(coupon) === activeFilter),
      ),
    [appliedCoupons, activeFilter],
  );

  const shouldShowUnavailableCoupons = groupedCoupons.some(
    (groupedCoupon) => groupedCoupon.category === "UNAVAILABLE",
  );

  const shouldShowBankOffers = groupedCoupons.some((groupedCoupon) => groupedCoupon.category === "PG_OFFERS");

  useEffect(() => {
    // coupons loaded event
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_COUPON_LOADED,
      eventType: "load",
      metaData: {
        couponData: { coupons_list: coupons },
      },
    });

    return () => {
      setCouponApplyError({ status: false, message: "" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCouponBack = useCallback(() => {
    setOpenDialog();
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_BACK_CLICK,
      eventType: "click",
    });
  }, [sendAnalyticsEvent, setOpenDialog]);

  const onApplyClick = async (couponCode: string, isStackable: boolean, couponTitle: string) => {
    // Achievable coupon — open the Discount Achiever tray with its impacted entities instead of applying
    const coupon = (coupons ?? []).find((c: any) => c?.coupon_details?.coupon_code === couponCode);
    const impactedEntities = coupon?.un_applicability_reason?.impacted_entities as
      | ImpactedEntity[]
      | undefined;
    const isAchievable =
      !coupon?.applicable &&
      coupon?.un_applicability_reason?.reason !== "UARC_002" &&
      coupon?.un_applicability_reason?.reason !== "UARC_007" &&
      Boolean(impactedEntities?.length);
    if (isAchievable) {
      setAchiever((prev) => ({
        ...prev,
        couponName: couponTitle || coupon?.coupon_details?.title || couponCode,
        couponCode,
        open: true,
      }));
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_DISCOUNT_ACHIEVER_OPENED,
        eventType: "click",
        metaData: {
          discountAchieverData: {
            couponCode,
            reason: coupon?.un_applicability_reason?.reason,
            blockCount: impactedEntities?.length ?? 0,
          },
        },
      });
      fetchInitialBlocks(impactedEntities ?? []);
      return;
    }
    setSelectedCoupon(couponTitle);
    if (Boolean(isStackable)) {
      setOpenReplaceCouponDialog(true);
      return;
    }
    await handleDiscountApply(couponCode, false, false, couponTitle);
    setOpenReplaceCouponDialog(false);
  };

  // Achiever tray CTA — add every selected variant to the cart, then apply the achieved coupon
  const handleAchieverAddSelected = async () => {
    if (achiever.submitting) return;
    // Selections live per block, so overlapping impacted entities can hold the same variant in two
    // blocks. Merge quantities by variantId — a payload with duplicate variant_id entries would let
    // the backend overwrite one with the other and short the cart.
    const mergedQuantities = achieverState.blocks.reduce<Record<string, number>>((acc, blockState) => {
      Object.entries(blockState.selectedItems).forEach(([variantId, quantity]) => {
        acc[variantId] = (acc[variantId] ?? 0) + quantity;
      });
      return acc;
    }, {});
    const items = Object.entries(mergedQuantities).map(([variantId, quantity]) => ({
      variantId,
      quantity,
    }));
    if (items.length === 0 || !achiever.couponCode) return;

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_DISCOUNT_ACHIEVER_SUBMIT_CLICKED,
      eventType: "click",
      metaData: {
        discountAchieverData: { couponCode: achiever.couponCode, itemCount: items.length, totalQuantity },
      },
    });

    setAchiever((prev) => ({ ...prev, submitting: true }));
    setOverlayLoading(true);
    try {
      const added = await handleAddItemWithResult(items, false);
      if (!added) {
        errorToast(t("something_went_wrong"));
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_DISCOUNT_ACHIEVER_ADD_TO_CART_FAILED,
          eventType: "flo_action",
          metaData: { discountAchieverData: { couponCode: achiever.couponCode, itemCount: items.length } },
        });
        return;
      }
      // Items are in the cart now — apply the discount
      await handleDiscountApply(achiever.couponCode, false, false, achiever.couponName);

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_DISCOUNT_ACHIEVER_COMPLETED,
        eventType: "flo_action",
        metaData: {
          discountAchieverData: { couponCode: achiever.couponCode, itemCount: items.length, totalQuantity },
        },
      });
      // Tray closes on success without a separate `closed` event — completion already captures the outcome
      achieverClosedByCompletionRef.current = true;
      setAchiever((prev) => ({ ...prev, open: false }));
    } finally {
      setAchiever((prev) => ({ ...prev, submitting: false }));
      setOverlayLoading(false);
    }
  };

  const page = searchParams.get("page")?.toLowerCase();
  const couponPageConfig =
    page === "cart"
      ? cartMetadata?.uiAttributes?.couponPageConfig
      : checkoutMetadata?.uiAttributes?.couponPageConfig;
  const partnerOffersUIConfig =
    page === "cart"
      ? cartMetadata?.uiAttributes?.partnerOffers
      : checkoutMetadata?.uiAttributes?.partnerOffers;
  const partnerOffersTitle = partnerOffersUIConfig?.section_title?.trim()
    ? partnerOffersUIConfig.section_title
    : "Partner Offers";
  const COUPON_FILTER_CONFIG = buildCouponFilterConfig(partnerOffersTitle);
  const discountCustomisations =
    page === "cart"
      ? cartMetadata?.uiAttributes?.discountConfig
      : checkoutMetadata?.uiAttributes?.discountConfig;
  const couponCustomisations = {
    ...couponPageConfig,
    primaryColor: couponPageConfig?.primaryColor ?? merchant?.colorPallet?.primaryColor ?? "#000000",
    textColor: couponPageConfig?.textColor ?? merchant?.colorPallet?.primaryTextColor ?? "#FFFFFF",
    backgroundColor: couponPageConfig?.backgroundColor ?? "#FFFFFF",
    cardRadius: couponPageConfig?.cardRadius ?? 6,
    filterRadius: couponPageConfig?.filterRadius ?? 6,
    buttonRadius: couponPageConfig?.buttonRadius ?? 6,
    inputRadius: couponPageConfig?.inputRadius ?? 10,
  } as CouponPageConfigType;

  return (
    <>
      <div className="relative flex h-full flex-col p-3 bg-zinc-100">
        <div className="flex flex-col gap-3">
          <div className="flex w-full flex-row items-center rounded-t-inherit gap-1.5">
            <div className="flex cursor-pointer items-center justify-center" onClick={handleCouponBack}>
              <Icon IconComponent={ArrowLeft} size="sm" iconProps={{ color: "text-coal-dark" }} />
            </div>
            <div className="flex flex-col">
              <h1 className="text-text-xs-semibold text-coal-dark">Coupons</h1>
              <p className="text-text-xs-medium text-zinc-800/60">
                {t("cart_value")}&nbsp;·&nbsp;{currencyFormatter(billing.sub_total)}
              </p>
            </div>
          </div>
          <div className="flex w-full items-stretch gap-2">
            <Input
              variant="outline"
              type="text"
              placeholder={t("enter_coupon_code")}
              name="coupon-code"
              id="coupon-code"
              maxLength={50}
              autoComplete="off"
              autoFocus={false}
              value={manualCoupon}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setManualCoupon(e.target.value.trim())}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                e.key === "Enter" && handleDiscountApply(manualCoupon, true)
              }
              startAdornment={
                <Tag
                  className="h-4 w-4 text-coal-dark mr-1"
                  style={{
                    color: couponCustomisations?.primaryColor,
                  }}
                />
              }
              className="h-[38px] min-w-0 flex-1 box-border"
              tabIndex={-1}
              error={couponApplyError?.status}
              wrapperStyle={{
                borderRadius: couponCustomisations?.inputRadius,
              }}
            />

            {Boolean(manualCoupon?.length) && (
              <button
                type="button"
                className="flex w-12 shrink-0 items-center justify-center bg-primary-dark"
                aria-label="Apply coupon"
                onClick={() => handleDiscountApply(manualCoupon, true)}
                style={{
                  backgroundColor: couponCustomisations?.primaryColor,
                  color: couponCustomisations?.textColor,
                  borderRadius: couponCustomisations?.inputRadius,
                }}>
                <Icon
                  IconComponent={ArrowRight}
                  size="sm"
                  iconProps={{ color: couponCustomisations?.textColor }}
                />
              </button>
            )}
          </div>
          {couponApplyError?.status && couponApplyError.message && (
            <div className="flex items-center gap-1">
              <WarningCircle className="-mt-[1px] h-3 w-3 shrink-0 text-ouch" />
              <label htmlFor="coupon-code" className="bg-transparent text-xs font-normal text-ouch">
                {couponApplyError.message}
              </label>
            </div>
          )}
          {!isPartnerOffersOnlyMode && (
            <CouponFilterButtons
              groupedCoupons={groupedCoupons}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              primaryColor={couponCustomisations?.primaryColor}
              customisations={couponCustomisations}
              filterConfig={COUPON_FILTER_CONFIG}
            />
          )}
        </div>
        <div className="pt-2.5 pb-6 flex-1 overflow-y-auto overflow-x-clip scrollbar-hide">
          {Boolean(nonRewardAppliedCoupons?.length) && activeFilter !== "PARTNER_OFFERS" && (
            <div>
              {!Boolean(isOnlyFreebieAutoApplied(nonRewardAppliedCoupons)) && (
                <CouponSummaryStrip
                  showRewards={false}
                  showSavings
                  showAppliedCoupons
                  savingItems={savingItems}
                  couponDisplayType={couponDisplayType}
                  handleDeleteCoupon={handleDeleteCoupon}
                  defaultOpen
                  parent="DIALOG"
                  customisations={discountCustomisations}
                />
              )}
            </div>
          )}
          {rewardDiscounts?.map((discount) => (
            <RewardDiscountCard discount={discount} onDelete={handleDeleteCoupon} t={t} key={discount.code} />
          ))}
          <div className="pt-3">
            <ProductSelectorSection
              productSelectorDiscounts={productSelectorDiscounts}
              t={t}
              customisations={couponCustomisations}
            />
          </div>
          {Boolean(coupons?.length) && !Boolean(isCouponsLoading) && activeFilter !== "PARTNER_OFFERS" && (
            <AvailableCouponsSection
              filteredCoupons={filteredCoupons}
              onApplyClick={onApplyClick}
              nonRewardAppliedCoupons={nonRewardAppliedCoupons}
              customisations={couponCustomisations}
            />
          )}
          <PartnerOffersSection
            partnerOffers={visiblePartnerOffers}
            partnerOffersDisplay={partnerOffersDisplay}
            activeFilter={activeFilter}
            onCollect={(offerId) => onCollectPartnerOffer?.([offerId])}
            onRemove={(offerId) => onRemovePartnerOffer?.([offerId])}
            customisations={couponCustomisations}
            branding={partnerOffersUIConfig?.branding}
          />
          {Boolean(coupons?.length) && !Boolean(isCouponsLoading) && activeFilter !== "PARTNER_OFFERS" && (
            <>
              <BankOffersSection
                filteredCoupons={filteredCoupons}
                shouldShow={shouldShowBankOffers}
                customisations={couponCustomisations}
              />
              <CalloutCardsSection
                filteredCoupons={filteredCoupons}
                t={t}
                customisations={couponCustomisations}
              />
              <UnavailableCouponsSection
                filteredCoupons={filteredCoupons}
                shouldShow={shouldShowUnavailableCoupons}
                onApplyClick={onApplyClick}
                nonRewardAppliedCoupons={nonRewardAppliedCoupons}
                t={t}
                customisations={couponCustomisations}
              />
            </>
          )}
        </div>
        <PartnerOffersStickyFooter
          className="-mx-3 -mb-3"
          partnerOffers={visiblePartnerOffers}
          partnerOffersDisplay={partnerOffersDisplay}
          activeFilter={activeFilter}
          onCollectAll={() => {
            const uncollected = visiblePartnerOffers
              .filter((o) => o.offer_state !== "COLLECTED" && o.offer_state !== "ALREADY_APPLIED")
              .map((o) => o.offer_id);
            if (uncollected.length) onCollectPartnerOffer?.(uncollected);
          }}
          primaryColor={couponCustomisations?.primaryColor}
          customisations={couponCustomisations}
        />
      </div>

      <GenericDialog
        isOpen={openReplaceCouponDialog}
        setIsOpen={(value: boolean) => setOpenReplaceCouponDialog(value)}
        translateAxis="y"
        customClass="overflow-scroll md:!top-auto md:absolute"
        dialogOverlay={true}
        modalType="REPLACE_COUPON">
        <ReplaceCouponDialog
          setIsOpen={() => setOpenReplaceCouponDialog(false)}
          applyCoupon={async () => {
            await handleDiscountApply(selectedCoupon, false);
            setOpenReplaceCouponDialog(false);
          }}
        />
      </GenericDialog>

      <FamilyTray
        open={achiever.open}
        onOpenChange={(open) => {
          // Only a user-driven dismiss (not the post-apply auto-close) counts as an abandonment
          if (!open && achiever.open) {
            if (achieverClosedByCompletionRef.current) {
              achieverClosedByCompletionRef.current = false;
            } else {
              const selectedCount = achieverState.blocks.reduce(
                (total, blockState) =>
                  total + Object.values(blockState.selectedItems).reduce((sum, qty) => sum + qty, 0),
                0,
              );
              const wasUnlocked =
                achieverState.blocks.length > 0 &&
                achieverState.blocks.every(
                  (blockState) =>
                    computeBlockFulfillment(blockState.block, blockState.selectedItems).isFulfilled,
                );
              sendAnalyticsEvent({
                eventName: analyticsEvents.FLO_DISCOUNT_ACHIEVER_CLOSED,
                eventType: "click",
                metaData: {
                  discountAchieverData: { couponCode: achiever.couponCode, wasUnlocked, selectedCount },
                },
              });
            }
            resetAchiever();
          }
          setAchiever((prev) => ({ ...prev, open }));
        }}
        couponName={achiever.couponName}
        onAddSelected={handleAchieverAddSelected}
        isSubmitting={achiever.submitting}
      />
    </>
  );
};

// Discount Achiever state is scoped to the Coupons dialog — unmounting on close resets it
const CouponDialog: React.FC<CouponDialogProps> = (props) => (
  <DiscountAchieverProvider>
    <CouponDialogContent {...props} />
  </DiscountAchieverProvider>
);

const CouponCardSkeletonLoader = () => {
  return (
    <div
      className={`group relative box-border flex w-full flex-col items-start rounded-2xl border-[1px] border-gray-light py-2 pl-3 pr-2`}>
      <div className="flex w-full flex-col space-y-2">
        <div className="skeleton-loader h-4 w-3/4 rounded"></div>
        <div className="skeleton-loader h-4 w-1/2 rounded"></div>
        <div className="skeleton-loader h-4 w-1/2 rounded"></div>
      </div>
    </div>
  );
};

export default CouponDialog;
